// src/hooks/useAgentStream.ts
import { useState, useCallback, useRef } from "react";
import { apiFetch, isAccountMismatchError } from "@/api/client";
import type { AgentStatus } from "@/components/chat/AgentProgress";
import type { InterimState } from "@/components/chat/InterimCard";
import type { ActionProposal } from "@/types/actionProposals";
import type { MessageSection } from "@/types/messages";
import type { PageContext } from "@/utils/pageContext";

export interface AgentSourceResult {
  agent_key: string;
  success: boolean;
  row_count?: number | null;
  duration_ms?: number;
  error?: string | null;
}

interface AgentResponse {
  response_type?: "answer" | "clarification-needed" | "error" | "partial";
  response_description?: string;
  clarification?: {
    reasoning?: string;
    followup?: string;
  };
  query_sql?: string;
  table_data?: { headers: string[]; rows: any[] };
  chart_data?: any;
  abstract_ir?: Record<string, unknown>;  // For dashboard refresh
  data_source_id?: string;                 // For dashboard refresh
  error?: {
    code: string;
    message: string;
    details?: any;
    recoverable?: boolean;
  };
  sources?: AgentSourceResult[];  // Multi-agent: per-agent results
  queries?: Array<{              // Multi-agent: per-source queries
    label: string;
    sql: string;
    params?: Record<string, unknown>;
    data_source_id?: string;
  }>;
  additional_results?: Array<{   // Multi-dataset split: extra result blocks
    dataset_key: string;
    dataset_label: string;
    table_data: { headers: string[]; rows: any[] };
    chart_data?: Record<string, unknown>;
    query_sql?: string;
  }>;
  sections?: MessageSection[];   // Structured answer sections (VER-256)
  closing_text?: string | null;  // Genuine model prose after the sections (assembler-verified; never synthetic)
  coverage_notice?: string | null;  // Periodic analyst: which sources were NOT analysed; rendered above the sections
  ratio_context?: string;  // Ratio calculation explanation (e.g., "AOV = revenue / transactions")
  follow_up_questions?: string[];  // Suggested follow-up questions from analysis
  question_fully_answered?: boolean;  // Whether the LLM considers the topic sufficiently explored
  multiple_choice?: { question?: string; options: string[] };  // Multiple choice clarification
  data_context?: { source_tables: string[]; time_period?: string; conditions: string[] };  // Data context transparency
  mode?: "normal" | "deepdive" | "deepdive_optimized";  // Pipeline mode (VER-255)
  scheduled_followup?: {  // Scheduled follow-up metadata (for result messages)
    automation_public_id: string;
    followup_question: string;
    original_context: string;
  };
}

export interface AgentSuggestion {
  agent_key: string;
  agent_label: string;
  matching_metrics: string[];
  message: string;
}

export interface FollowupSuggestedInfo {
  followup_question: string;
  analyst_instructions: string;
  analysis_context: string;
  schedule_date: string;
  schedule_hour: number;
  reason: string;
  /** Server-authored English note when the date was moved forward out of an
   *  unfinished period. Absent when the proposed date already stood. */
  schedule_note?: string | null;
  /** The calendar period this follow-up needs complete, e.g. "2026-08".
   *  Sent back on confirm so the backend can re-check it. */
  requires_complete_period?: string | null;
  data_source_public_id: string;
  workspace_public_id: string;
  conversation_public_id: string;
  agent_key: string;
}

export interface SegmentSuggestedInfo {
  name: string;
  description?: string;
  table_fqn: string;
  definition: Record<string, unknown>;
  data_source_public_id: string;
  workspace_public_id: string;
  count_column?: string;
}

export interface RecommendationEntityRef {
  platform: string;
  level: string;
  id: string;
  name: string;
}

export interface RecommendationCurrentState {
  daily_budget?: number | null;
  roas?: number | null;
  cpa?: number | null;
  ctr?: number | null;
  trend_7d?: number | null;
}

export interface RecommendationProposedChange {
  daily_budget?: number | null;
  change_pct?: number | null;
  expected_impact?: string | null;
}

export interface AcceptedRecommendation {
  action: "increase_budget" | "decrease_budget" | "pause" | "reactivate" | "monitor";
  entity: RecommendationEntityRef;
  current_state: RecommendationCurrentState;
  proposed_change: RecommendationProposedChange;
  confidence: "high" | "medium" | "low";
  confidence_factors: string[];
  risk_factors: string[];
  validation_required?: string[];
  blocking?: boolean;
}

export interface RejectedRecommendation {
  index: number;
  entity_id?: string;
  action?: string;
  reasons: string[];
}

export interface RecommendationCrossCheckSummary {
  retail_alignment?: string;
  seasonality_risk?: string;
  summary_text?: string | null;
  correlation?: number | null;
  external_factors?: string[];
}

export interface RecommendationSuggestedInfo {
  rendered_recommendation_id: string;
  campaign_analysis_id: string;
  accepted: AcceptedRecommendation[];
  rejected: RejectedRecommendation[];
  validation_errors: string[];
  total_budget_impact: {
    current_daily_total: number;
    proposed_daily_total: number;
    net_change_pct: number;
  } | null;
  cross_check_summary: RecommendationCrossCheckSummary | null;
}

export interface StreamResult {
  conversationId: string | null;
  userMessageId: string | null;
  assistantMessageId: string | null;
  response: AgentResponse | null;
  suggestions: AgentSuggestion[] | null;
  followupSuggested: FollowupSuggestedInfo | null;
  segmentSuggested: SegmentSuggestedInfo | null;
  recommendationSuggested: RecommendationSuggestedInfo | null;
  /** Action-proposal confirm cards emitted this run (in call order). */
  actionProposals: ActionProposal[] | null;
  error: string | null;
  errorCode: string | null;
  errorRecoverable: boolean;
}

export interface StreamPayload {
  account_id: string;
  workspace_id: string;
  user_id: string;
  message: string;
  conversation_id?: string | null;
  data_source_id?: string;
  data_source_public_id?: string | null;
  agent_key?: string;
  routing_mode?: "auto" | "pinned";
  mode?: "normal" | "deepdive" | "deepdive_optimized";  // Pipeline mode (VER-255)
  /**
   * Chat surface discriminator (plan 2026-07-28). Only honoured at conversation
   * creation; existing conversations derive their surface from the row. Omit or
   * "insights" for the data chat; "assistant" for the global platform assistant.
   */
  surface?: "insights" | "assistant";
  /**
   * Structured current-page context (plan 2026-07-28 §5, step 3.2). Sent only by
   * the assistant surface so "this source"/"this segment" resolves without the
   * user naming it. Structured fields only — the backend validates module/submenu
   * against the menu registry and resolves entity_id account-scoped (untrusted
   * hint). Derived at send time via derivePageContext(pathname).
   */
  page_context?: PageContext;
  /**
   * Public id (`pan_…`) of the analysis this turn is about (plan 2026-08-21
   * step 1). Sent by the document ask-tab, where the conversation is by
   * definition NOT the analysis. The backend resolves it against the caller's
   * account AND the membership-checked workspace, and a reference that does
   * not resolve is a 404 rather than a silent drop — so this is a claim the
   * server verifies, never a hint it trusts.
   */
  analysis_public_id?: string | null;
  scope_type?: string | null;   // e.g., "insight" for insight chats
  scope_id?: string | null;     // e.g., item public_id (ditem_...) for insight chats
  /**
   * Ask for a NEW conversation on this scope instead of the backend's
   * find-or-create reuse (plan 2026-08-27 — a second analysis of one chart).
   * Only meaningful when `conversation_id` is absent; a request that names a
   * conversation never reaches the flag server-side. Declared here because the
   * payload is passed as a variable, so TypeScript's excess-property check
   * would not catch its absence — the field would ship while this interface,
   * which documents the wire contract, stayed silent about it.
   */
  new_conversation?: boolean;
  dashboard_filters?: {
    start_date?: string;
    end_date?: string;
    segment_filters?: Record<string, unknown>;
  };
}

export interface RoutingInfo {
  strategy: string;
  reasoning: string;
  confidence: number;
  routing_stage: number;
  duration_ms: number;
  chosen_agent: string | null;
  chosen_agent_display: string | null;
  available_agents: Array<{
    agent_key: string;
    display_name: string;
    ds_key: string | null;
  }>;
  targets?: Array<{
    agent_key: string;
    display_name: string;
    ds_key: string | null;
  }>;
}

interface UseAgentStreamOptions {
  onInit?: (data: { conversationId: string; userMessageId: string; assistantMessageId?: string }) => void;
  onStatus?: (status: AgentStatus) => void;
  onStepComplete?: (status: AgentStatus) => void;
  onFinal?: (response: AgentResponse) => void;
  onTextDelta?: (content: string) => void;
  /**
   * End of the current turn's provisional text (text_boundary contract).
   * "commit" = keep the streamed tail; "discard" = it was narration before a
   * real tool call (VER-255) — remove the uncommitted tail from the UI.
   */
  onTextBoundary?: (action: "commit" | "discard") => void;
  onError?: (error: { code: string; message: string }) => void;
  onRouting?: (routing: RoutingInfo) => void;
  onSuggestion?: (suggestions: AgentSuggestion[]) => void;
}

// --- Error mapping helpers ---

const INACTIVITY_TIMEOUT_MS = 60_000;
const INACTIVITY_TIMEOUT_DEEPDIVE_MS = 300_000; // 5 min for deepdive extended thinking
const INACTIVITY_CHECK_INTERVAL_MS = 5_000;

function getHttpErrorMessage(status: number): string {
  switch (status) {
    case 400: return "Some information needed to process your question is missing. Check that a data source is connected.";
    case 401: return "Your session has expired. Please sign in again.";
    case 403: return "You don't have access to this agent or data source.";
    case 404: return "This conversation is no longer available.";
    case 422: return "Your request couldn't be processed. Please check your input.";
    default:
      if (status >= 500) return "The server is temporarily unavailable. Please try again later.";
      return `Something went wrong (${status}). Please try again.`;
  }
}

function getHttpErrorCode(status: number): string {
  if (status === 401) return "AUTH_EXPIRED";
  if (status === 403) return "ACCESS_DENIED";
  return "STREAM_ERROR";
}

export function useAgentStream(options: UseAgentStreamOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interim, setInterim] = useState<InterimState | null>(null);
  const [routing, setRouting] = useState<RoutingInfo | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const sendMessage = useCallback(
    async (payload: StreamPayload): Promise<StreamResult> => {
      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setStatus(null);
      setError(null);
      setInterim(null);
      setRouting(null);

      const result: StreamResult = {
        conversationId: null,
        userMessageId: null,
        assistantMessageId: null,
        response: null,
        suggestions: null,
        followupSuggested: null,
        segmentSuggested: null,
        recommendationSuggested: null,
        actionProposals: null,
        error: null,
        errorCode: null,
        errorRecoverable: true,
      };

      // Track last event time for inactivity timeout
      const isDeepDive = payload.mode === "deepdive" || payload.mode === "deepdive_optimized";
      const inactivityTimeout = isDeepDive ? INACTIVITY_TIMEOUT_DEEPDIVE_MS : INACTIVITY_TIMEOUT_MS;
      let lastEventTime = Date.now();
      let timedOut = false;
      let inactivityChecker: ReturnType<typeof setInterval> | null = null;

      try {
        const response = await apiFetch("/chats/stream", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        // HTTP error mapping (stap 11)
        if (!response.ok) {
          const msg = getHttpErrorMessage(response.status);
          const code = getHttpErrorCode(response.status);
          result.error = msg;
          result.errorCode = code;
          result.errorRecoverable = response.status !== 401 && response.status !== 403;
          setError(msg);
          optionsRef.current.onError?.({ code, message: msg });
          return result;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        const processEvent = (eventType: string, data: string) => {
          try {
            const parsed = JSON.parse(data);

            switch (eventType) {
              case "init":
                result.conversationId = parsed.conversation_id;
                result.userMessageId = parsed.user_message_id;
                if (parsed.assistant_message_id) {
                  result.assistantMessageId = parsed.assistant_message_id;
                }
                optionsRef.current.onInit?.({
                  conversationId: parsed.conversation_id,
                  userMessageId: parsed.user_message_id,
                  assistantMessageId: parsed.assistant_message_id,
                });
                break;

              case "routing":
                setRouting(parsed as RoutingInfo);
                optionsRef.current.onRouting?.(parsed as RoutingInfo);
                break;

              case "status":
                setStatus(parsed as AgentStatus);
                optionsRef.current.onStatus?.(parsed as AgentStatus);
                break;

              case "step_complete":
                // Machine event: `step` is the raw tool key (the chat route
                // sets step=tool_name), so it must never drive the visible
                // status label — the friendly `status` label ("Querying
                // data...") stays up while the tool runs. Consumers that want
                // the machine event still get the callback.
                optionsRef.current.onStepComplete?.(parsed as AgentStatus);
                break;

              case "interim":
                setInterim({
                  phase: parsed.phase,
                  message: parsed.message,
                  progress: parsed.progress,
                });
                break;

              case "text_delta": {
                const content = parsed.content || parsed.data?.content || "";
                if (content) {
                  optionsRef.current.onTextDelta?.(content);
                }
                break;
              }

              case "text_boundary": {
                const action = parsed.action === "discard" ? "discard" : "commit";
                optionsRef.current.onTextBoundary?.(action);
                break;
              }

              case "final":
                result.response = parsed.response;
                setInterim(null);  // Auto-dismiss interim card
                setStatus({
                  type: "status",
                  step: "Done",
                  progress: 100,
                });
                optionsRef.current.onFinal?.(parsed.response);
                break;

              case "saved":
                result.assistantMessageId = parsed.assistant_message_id;
                break;

              case "suggestion":
                const suggestions = parsed.suggestions || [];
                result.suggestions = suggestions;
                optionsRef.current.onSuggestion?.(suggestions);
                break;

              case "followup_suggested":
                result.followupSuggested = {
                  followup_question: parsed.followup_question,
                  analyst_instructions: parsed.analyst_instructions,
                  analysis_context: parsed.analysis_context,
                  schedule_date: parsed.schedule_date,
                  schedule_hour: parsed.schedule_hour,
                  reason: parsed.reason,
                  schedule_note: parsed.schedule_note ?? null,
                  requires_complete_period: parsed.requires_complete_period ?? null,
                  data_source_public_id: parsed.data_source_public_id,
                  workspace_public_id: parsed.workspace_public_id,
                  conversation_public_id: parsed.conversation_public_id,
                  agent_key: parsed.agent_key,
                };
                break;

              case "segment_suggested":
                result.segmentSuggested = {
                  name: parsed.name,
                  description: parsed.description,
                  table_fqn: parsed.table_fqn,
                  definition: parsed.definition,
                  data_source_public_id: parsed.data_source_public_id,
                  workspace_public_id: parsed.workspace_public_id,
                  count_column: parsed.count_column,
                };
                break;

              case "action_proposed":
                if (parsed.proposal) {
                  result.actionProposals = [
                    ...(result.actionProposals ?? []),
                    parsed.proposal as ActionProposal,
                  ];
                }
                break;

              case "recommendation_suggested":
                result.recommendationSuggested = {
                  rendered_recommendation_id: parsed.rendered_recommendation_id,
                  campaign_analysis_id: parsed.campaign_analysis_id,
                  accepted: parsed.accepted || [],
                  rejected: parsed.rejected || [],
                  validation_errors: parsed.validation_errors || [],
                  total_budget_impact: parsed.total_budget_impact || null,
                  cross_check_summary: parsed.cross_check_summary || null,
                };
                break;

              case "error":
                result.error = parsed.message;
                result.errorCode = parsed.code || "STREAM_ERROR";
                result.errorRecoverable = parsed.recoverable !== false;
                setError(parsed.message);
                optionsRef.current.onError?.(parsed);
                break;

              case "end":
                // Stream ended
                break;
            }
          } catch (e) {
            console.error("Failed to parse SSE event:", e, data);
          }
        };

        // Start inactivity timeout checker (stap 10)
        inactivityChecker = setInterval(() => {
          if (Date.now() - lastEventTime > inactivityTimeout) {
            timedOut = true;
            controller.abort();
          }
        }, INACTIVITY_CHECK_INTERVAL_MS);

        // Read the stream
        // NOTE: currentEvent/currentData MUST persist across reader.read() calls.
        // A single SSE event can span multiple chunks if the network splits them.
        let currentEvent = "";
        let currentData = "";

        const processLines = (lines: string[]) => {
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              // New event starts — flush any orphaned previous event
              if (currentEvent && currentData) {
                processEvent(currentEvent, currentData);
              }
              currentEvent = line.slice(7).trim();
              currentData = "";
            } else if (line.startsWith("data: ")) {
              currentData += line.slice(6);
            } else if (line === "" && currentEvent && currentData) {
              // Empty line marks end of event
              processEvent(currentEvent, currentData);
              currentEvent = "";
              currentData = "";
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          lastEventTime = Date.now();
          buffer += decoder.decode(value, { stream: true });

          // Process complete events
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          processLines(lines);
        }

        // Process any remaining data in buffer
        if (buffer.trim()) {
          const lines = buffer.split("\n");
          processLines(lines);
        }

        // Incomplete stream detection (stap 9)
        // If stream ended without a final response or error, something went wrong
        if (!result.response && !result.error) {
          const msg = "The response couldn't be fully loaded. Please try again.";
          result.error = msg;
          result.errorCode = "INCOMPLETE_STREAM";
          result.errorRecoverable = true;
          setError(msg);
          optionsRef.current.onError?.({ code: "INCOMPLETE_STREAM", message: msg });
        }
      } catch (e) {
        if ((e as Error).name === "AbortError" || isAccountMismatchError(e)) {
          // Distinguish between user abort and inactivity timeout. An account
          // mismatch aborts the request the same way — the mismatch gate
          // explains the situation, so this stream must not surface its own
          // error on top of it.
          if (timedOut) {
            const msg = "The connection to the server was lost. Please try again.";
            result.error = msg;
            result.errorCode = "STREAM_TIMEOUT";
            result.errorRecoverable = true;
            setError(msg);
            optionsRef.current.onError?.({ code: "STREAM_TIMEOUT", message: msg });
          }
          // User-initiated abort: return silently
          return result;
        }

        const errorMessage = (e as Error).message || "An unexpected error occurred.";
        result.error = errorMessage;
        result.errorCode = "STREAM_ERROR";
        result.errorRecoverable = true;
        setError(errorMessage);
        optionsRef.current.onError?.({ code: "STREAM_ERROR", message: errorMessage });
      } finally {
        if (inactivityChecker) clearInterval(inactivityChecker);
        // Only tear down the stream state if THIS invocation still owns the
        // controller. An abort-then-send in the same tick (AssistantContext's
        // ask(): newConversation() -> abort() -> send()) starts the next turn
        // synchronously, so it has already installed its own controller by the
        // time this aborted turn's finally runs. Without the owner check we
        // would null out the live turn's controller — leaving it unabortable
        // and reporting isLoading=false while it is still streaming.
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
          abortControllerRef.current = null;
        }
      }

      return result;
    },
    [] // Stable identity — callbacks accessed via optionsRef
  );

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setStatus(null);
    setInterim(null);
  }, []);

  return {
    isLoading,
    status,
    error,
    interim,
    routing,
    sendMessage,
    abort,
  };
}

export type { AgentResponse, UseAgentStreamOptions };
